import { CaretDown, Export } from "@phosphor-icons/react";
import clsx from "clsx";
import { useState } from "react";
import { toast } from "react-toastify";

import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "common/components/ui/Dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from "common/components/ui/Drawer";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuList,
  DropdownMenuTrigger,
} from "common/components/ui/DropdownMenu";
import Skeleton from "common/components/ui/Skeleton";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useIntegrationSettings from "common/datahooks/useIntegrationSettings";
import { get } from "common/helpers/HTTP";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type {
  CrmType,
  EmailOutreachType,
  LeadsSearchParams,
  SelectedFacetParam,
} from "common/types";

import { INTEGRATIONS_MAP } from "../../../../integrations/constants";
import useLeadExports from "../../../datahooks/useLeadExports";
import OutreachExportModal from "./OutreachExportModal";

import InstantlyIcon from "assets/icons/instantly.svg?react";
import HubspotIcon from "assets/icons/integrations/hubspot-simple.svg?react";
import PipedriveIcon from "assets/icons/integrations/pipedrive-simple.svg?react";
import SmartleadIcon from "assets/icons/smartlead.svg?react";

const crmIntegrationExports = [
  { key: "hubspot", icon: HubspotIcon },
  { key: "pipedrive", icon: PipedriveIcon },
] as const;

const outreachIntegrationsExport = [
  { key: "instantly", icon: InstantlyIcon },
  { key: "smartlead", icon: SmartleadIcon },
] as const;

async function pollExportJob(
  workspaceId: string,
  jobId: string,
  integrationName: string,
) {
  const { job } = await get(
    `workspaces/${workspaceId}/integrations/jobs/${jobId}`,
  );
  if (job.state === "in_progress") {
    setTimeout(() => pollExportJob(workspaceId, jobId, integrationName), 8000);
  } else if (job.state === "success") {
    toast.success(`Data exported to ${integrationName} successfully`);
  } else if (job.state === "failed") {
    toast.error(
      `Data export to ${integrationName} failed with code ${job.code}`,
    );
  }
}

interface ExportLeadsProps {
  searchParams: LeadsSearchParams<SelectedFacetParam>;
  totalLeadsCount: number;
  isFetching: boolean;
}

export default function ExportLeads({
  searchParams,
  totalLeadsCount,
  isFetching,
}: ExportLeadsProps) {
  const isDesktopOrTablet = useTwBreakpoint("md");
  const { id: workspaceId } = useSelectedWorkspaceContext();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [exportUrl, setExportUrl] = useState<string>();
  const [selectedOutreachType, setSelectedOutreachType] =
    useState<EmailOutreachType>();

  const { integrationSettings } = useIntegrationSettings();
  const {
    instantly,
    smartlead,
    rb2b: _rb2b,
    ...crmIntegrations
  } = integrationSettings || {};

  const isDisabled = isFetching || totalLeadsCount === 0;

  const hasConnectedOutreachIntegrations = instantly || smartlead;
  const hasInstalledCrmIntegrations = Object.values(crmIntegrations).some(
    (settingsItem) => !("install_url" in settingsItem),
  );

  const labelComponent = (
    <>
      Export{" "}
      {isFetching ? <Skeleton className="h-5 w-8" /> : `(${totalLeadsCount})`}
    </>
  );

  const {
    exportLeads,
    exportLeadsToCrmIntegration,
    isExportingLeads,
    isExportingLeadsToCrmIntegration,
  } = useLeadExports();

  function onExport() {
    exportLeads({ searchParams }).then((url) => {
      setExportUrl(url);
    });

    toast("Your export is preparing, you will be notified when it's ready");
  }

  function onCrmIntegrationExport(crmType: CrmType, name: string) {
    exportLeadsToCrmIntegration({ searchParams, crmType })
      .then((jobId) => {
        if (jobId) {
          // poll the job to show a notification when it's finished
          pollExportJob(workspaceId, jobId, name);
        } else {
          toast.success(`Data exported to ${name} successfully`);
        }
      })
      .catch(() => {
        toast.error(`Couldn't export data to ${name}`);
      });
    toast("Your export is preparing, you will be notified when it's ready");
  }

  const Component = isDesktopOrTablet ? Dialog : Drawer;
  const Content = isDesktopOrTablet ? DialogContent : DrawerContent;
  const Title = isDesktopOrTablet ? DialogTitle : DrawerTitle;
  const Description = isDesktopOrTablet ? DialogDescription : DrawerDescription;

  return (
    <>
      {hasConnectedOutreachIntegrations || hasInstalledCrmIntegrations ? (
        <div className="group flex rounded-full bg-purple-50">
          <button
            type="button"
            className="flex gap-x-2 items-center rounded-l-full pl-5 pr-2.5 text-button-sm text-purple-600 transition-colors duration-300 hover:bg-purple-100 disabled:cursor-not-allowed disabled:bg-black-200 disabled:text-black-400 disabled:border-black-200"
            onClick={onExport}
            disabled={isDisabled || isExportingLeads}
          >
            <Export weight="fill" size={20} className="-ml-2.5" />
            {labelComponent}
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger
              aria-label="more-start-campaign-options"
              disabled={isDisabled}
              className={clsx(
                "group relative h-10 rounded-r-full p-2.5 transition-colors text-purple-600 duration-300 data-[state=open]:bg-purple-100 hover:bg-purple-100",
                "disabled:cursor-not-allowed disabled:bg-black-200 disabled:text-black-400 disabled:border-black-200",
                "before:absolute before:left-0 before:h-5 before:w-px before:bg-purple-100 before:opacity-100 before:transition-opacity before:duration-300 before:ease-in-out before:group-hover:opacity-0",
              )}
            >
              <CaretDown className="size-5 transition-transform group-data-[state=open]:rotate-180" />
            </DropdownMenuTrigger>
            <DropdownMenuContent className="z-aboveDialog" align="end">
              <DropdownMenuList>
                {crmIntegrationExports.map(({ key, icon: Icon }) => {
                  if (!("install_url" in integrationSettings[key])) {
                    const { title } = INTEGRATIONS_MAP[key];

                    return (
                      <DropdownMenuItem
                        key={key}
                        onClick={() => onCrmIntegrationExport(key, title)}
                        disabled={isExportingLeadsToCrmIntegration}
                      >
                        <Icon />
                        Export to {title}
                      </DropdownMenuItem>
                    );
                  }
                })}

                {outreachIntegrationsExport.map(({ key, icon: Icon }) => {
                  if (integrationSettings[key]) {
                    return (
                      <DropdownMenuItem
                        key={key}
                        onClick={() => {
                          setSelectedOutreachType(key);
                          setIsModalOpen(true);
                        }}
                      >
                        <Icon />
                        Export to <span className="capitalize">{key}</span>
                      </DropdownMenuItem>
                    );
                  }
                })}
              </DropdownMenuList>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ) : (
        <Button
          variant="secondary-purple"
          leftIcon={<Export weight="fill" />}
          disabled={isDisabled}
          isLoading={isExportingLeads}
          onClick={onExport}
        >
          {labelComponent}
        </Button>
      )}

      {hasConnectedOutreachIntegrations && (
        <OutreachExportModal
          key={selectedOutreachType}
          type={selectedOutreachType}
          isOpen={isModalOpen}
          setIsOpen={setIsModalOpen}
          searchParams={searchParams}
        />
      )}

      <Component
        open={!!exportUrl}
        onOpenChange={(open) => {
          if (!open) {
            setExportUrl(undefined);
          }
        }}
      >
        <Content className="md:w-[448px]">
          <Title>Your export is ready</Title>
          <Description className="mb-8">
            Click the button below to download your export in CSV format
          </Description>
          <Button
            size={isDesktopOrTablet ? "md" : "lg"}
            onClick={() => {
              window.open(exportUrl);
              setExportUrl(null);
            }}
          >
            Download
          </Button>
        </Content>
      </Component>
    </>
  );
}
